import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useListTwins, useDevices } from '@/api/devices'

interface Props {
  tenantId: string | null
}

function relTime(dateStr?: string): string {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const secs = Math.round(diff / 1000)
  if (secs < 60) return `${secs}s fa`
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m fa`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h fa`
  return `${Math.round(hrs / 24)}g fa`
}

function extractField(state: Record<string, unknown>, key: string): string {
  const v = state[key]
  if (v == null) return '—'
  if (typeof v === 'object' && v !== null) {
    const obj = v as Record<string, unknown>
    return String(obj.version ?? obj.distro ?? JSON.stringify(v))
  }
  return String(v)
}

const card: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius)',
  overflow: 'hidden',
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid var(--color-border)',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 13,
}

export function Twins({ tenantId }: Props) {
  const navigate = useNavigate()
  const { data: twinsData, isLoading, error } = useListTwins(tenantId)
  const { data: devicesData } = useDevices(tenantId)
  const [filter, setFilter] = useState<'all' | 'out_of_sync'>('all')
  const [search, setSearch] = useState('')

  const twins = twinsData?.results ?? []
  const deviceMap = new Map((devicesData?.data ?? []).map(d => [d.id, d.name]))

  const outOfSyncCount = twins.filter(t => !t.in_sync).length

  const filtered = twins
    .filter(t => filter === 'all' || !t.in_sync)
    .filter(t => {
      if (!search) return true
      const q = search.toLowerCase()
      const name = (deviceMap.get(t.device_id) ?? '').toLowerCase()
      return t.device_id.toLowerCase().includes(q) || name.includes(q)
    })

  if (!tenantId) {
    return <div className="empty-state">Seleziona un tenant per visualizzare i digital twin.</div>
  }

  if (isLoading) {
    return <div className="empty-state">Caricamento digital twin…</div>
  }

  if (error) {
    return <div className="empty-state" style={{ color: 'var(--color-error)' }}>Errore nel caricamento dei twin.</div>
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Digital Twins</h1>
          <p className="page-subtitle">Stato riportato e desiderato di tutti i device</p>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Totale', value: twins.length, color: 'var(--color-text)' },
          { label: 'In sync', value: twins.length - outOfSyncCount, color: '#22c55e' },
          { label: 'Out of sync', value: outOfSyncCount, color: outOfSyncCount > 0 ? '#fbbf24' : 'var(--color-text-muted)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ ...card, padding: '12px 20px', minWidth: 110 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Cerca per nome o ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text)',
            padding: '7px 12px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            width: 240,
          }}
        />
        {(['all', 'out_of_sync'] as const).map(f => (
          <button
            key={f}
            className={`btn ${filter === f ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: 12 }}
            onClick={() => setFilter(f)}
          >
            {{ all: 'Tutti', out_of_sync: `Out of sync (${outOfSyncCount})` }[f]}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          {filter === 'out_of_sync'
            ? 'Tutti i twin sono in sync.'
            : twins.length === 0
              ? 'Nessun digital twin disponibile. I device inviano lo stato via MQTT gateway una volta configurati.'
              : 'Nessun risultato per la ricerca corrente.'}
        </div>
      ) : (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thStyle}>Device</th>
                <th style={thStyle}>Sync</th>
                <th style={thStyle}>Firmware</th>
                <th style={thStyle}>OS</th>
                <th style={thStyle}>Kanto</th>
                <th style={thStyle}>Delta</th>
                <th style={thStyle}>Aggiornato</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(twin => {
                const state = twin.reported?.state ?? {}
                const deviceName = deviceMap.get(twin.device_id)
                const deltaCount = Object.keys(twin.delta ?? {}).length
                return (
                  <tr
                    key={twin.device_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/devices/${twin.device_id}`)}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={tdStyle}>
                      {deviceName ? (
                        <div style={{ fontWeight: 500 }}>{deviceName}</div>
                      ) : null}
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: deviceName ? 'var(--color-text-muted)' : 'var(--color-text)' }}>
                        {twin.device_id.slice(0, 8)}…
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {twin.in_sync ? (
                        <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 500 }}>● Synced</span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#fbbf24', fontWeight: 500 }}>⚠ Pending</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {extractField(state, 'firmware')}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>
                      {extractField(state, 'os')}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {extractField(state, 'kanto_version')}
                    </td>
                    <td style={tdStyle}>
                      {deltaCount > 0 ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)',
                          color: '#fbbf24', fontSize: 11, fontWeight: 600,
                        }}>
                          {deltaCount} {deltaCount === 1 ? 'modifica' : 'modifiche'}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--color-text-muted)', fontSize: 12 }}>
                      {relTime(twin.reported?.updated_at)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
